<script setup>
import { computed } from 'vue'
import { trans } from 'laravel-vue-i18n'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import { FORM_COLOR_MAP } from '../../../composables/useReservationsCalendar.js'

const props = defineProps({
    request: { type: Object, required: true },
    // { id, type, client, when, formName, formColor, fromTxt, toTxt, reason }
})

const emit = defineEmits(['approve', 'reject'])

const color = computed(() => FORM_COLOR_MAP[props.request.reservations?.reservation_forms?.theme?.color] ?? '#6B7280')

const typeLabel = computed(() => ({
    time:   trans('reservations.request_time_label'),
    date:   trans('reservations.request_date_label'),
    cancel: trans('reservations.request_cancel_label'),
}))

const requestType = computed(() => {
    if (props.request.action === 'cancel' || props.request.cancel_requested) return 'cancel'
    if (props.request.type) return props.request.type
    if (props.request.new_date) return 'date'
    return 'time'
})

function fmtDate(dateStr) {
    if (!dateStr) return ''
    const [y, m, d] = dateStr.split('-')
    return `${parseInt(d)}. ${parseInt(m)}. ${y}`
}

function fmtTime(t) {
    return t ? t.substring(0, 5) : ''
}

const res = computed(() => props.request.reservations ?? {})

const fromTxt = computed(() => {
    if (props.request.fromTxt) return props.request.fromTxt
    if (!res.value.date && !res.value.time_start) return ''
    const date = fmtDate(res.value.date)
    const time = res.value.time_start ? ` ${fmtTime(res.value.time_start)}–${fmtTime(res.value.time_end)}` : ''
    return date + time
})

const toTxt = computed(() => {
    if (props.request.toTxt) return props.request.toTxt
    if (!props.request.new_date && !props.request.new_time_start) return ''
    const date = fmtDate(props.request.new_date)
    const time = props.request.new_time_start ? ` ${fmtTime(props.request.new_time_start)}–${fmtTime(props.request.new_time_end)}` : ''
    return date + time
})

const client   = computed(() => res.value.name ?? '')
const formName = computed(() => {
    const form = res.value.reservation_forms
    return form?.form_name ?? form?.name ?? ''
})
const when = computed(() => props.request.created_at ? new Date(props.request.created_at).toLocaleDateString('cs') : '')
</script>

<template>
    <div
        class="border border-gray-100 dark:border-neutral-800 rounded-[13px] p-3.5 flex flex-col gap-2 mb-2.5 last:mb-0"
        :style="{ borderLeftColor: color, borderLeftWidth: '3px' }"
    >
        <div class="flex items-center gap-2">
            <Tag
                :value="typeLabel[requestType]"
                :severity="requestType === 'cancel' ? 'danger' : requestType === 'date' ? 'secondary' : 'info'"
                class="!text-[10px] flex-shrink-0"
            />
            <span class="text-[13px] font-bold text-gray-900 dark:text-white flex-1 min-w-0 truncate">{{ client }}</span>
            <span class="text-[11px] text-gray-300 dark:text-neutral-600 font-medium flex-shrink-0">{{ when }}</span>
        </div>

        <!-- Form name -->
        <div class="flex items-center gap-1.5 text-[11.5px] text-gray-400 dark:text-neutral-500">
            <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :style="{ background: color }"/>
            {{ formName }}
        </div>

        <!-- Change detail -->
        <div class="flex items-center gap-2.5 text-[13px] flex-wrap">
            <span class="text-gray-400 dark:text-neutral-500 line-through decoration-gray-300 dark:decoration-neutral-600">{{ fromTxt }}</span>
            <template v-if="requestType !== 'cancel'">
                <span class="text-gray-300 dark:text-neutral-600">→</span>
                <span class="text-gray-900 dark:text-white font-bold">{{ toTxt }}</span>
            </template>
            <span v-else class="text-[#D9544A] font-bold">{{ $t('reservations.request_cancel_action') }}</span>
        </div>

        <!-- Reason -->
        <div v-if="request.message || request.reason" class="text-[12px] text-gray-600 dark:text-neutral-300 bg-gray-50 dark:bg-neutral-800 rounded-[9px] px-3 py-2 leading-relaxed">
            <span class="font-semibold text-gray-400 dark:text-neutral-500">{{ $t('reservations.reason_label') }}:</span> {{ request.message ?? request.reason }}
        </div>

        <div class="flex gap-2">
            <Button
                :label="$t('reservations.btn_approve')"
                icon="pi pi-check"
                severity="success"
                size="small"
                class="flex-1"
                @click="emit('approve', request.id)"
            />
            <Button
                :label="$t('reservations.btn_reject')"
                icon="pi pi-times"
                severity="danger"
                size="small"
                outlined
                class="flex-1"
                @click="emit('reject', request.id)"
            />
        </div>
    </div>
</template>
